/**
 * Fraud review — four read-only detective queues, one per correlation signal, as tabs.
 *
 * These are correlations, not verdicts, and the whole screen is written not to imply otherwise. A
 * shared sign-in IP is a household, an office or a carrier NAT far more often than it is a farm;
 * unusual request volume is usually legitimate; a run of refunds can be a bad month. The API offers
 * NO enforcement route from here at MVP — no revoke, no suspend, no claw-back — so there is no
 * button to add. Each list exists to put a human in front of a short queue, not to act on an
 * inference. Three of the four rows carry a user id, so each can drill into that account's
 * linked-accounts graph — itself an audited read, gated behind a deliberate click.
 *
 * Only the active tab's panel is mounted, so only its query runs: switching to a tab is what fetches
 * it, and a signed-out reviewer (guarded upstream) never fires any of them.
 */
import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchFraudFlags,
  fetchInviteBurstFlags,
  fetchRefundFlags,
  fetchVelocityFlags,
  type Page,
} from '@/lib/admin-api';
import {
  formatTimestamp,
  type InviteBurstFlag,
  type ReferralFraudFlag,
  type RefundAbuseFlag,
  type VelocityFlag,
} from '@/lib/wire';
import { PageHeader } from '@/components/AppShell';
import { EmptyState, ErrorState, Loading } from '@/components/DataState';
import { LinkedAccountsDisclosure } from '@/components/LinkedAccounts';
import { Pager } from '@/components/Pager';
import { Tabs, type TabItem } from '@/components/Tabs';
import { Badge } from '@/components/ui/badge';

const TABS: TabItem[] = [
  { id: 'referrals', label: 'Referral flags' },
  { id: 'velocity', label: 'Shared IPs' },
  { id: 'invite-bursts', label: 'Request bursts' },
  { id: 'refunds', label: 'Refunds' },
];

const PANEL_ID = 'fraud-panel';

export function Fraud() {
  const [tab, setTab] = useState<TabItem['id']>('referrals');

  return (
    <>
      <PageHeader title="Fraud review">
        Correlation signals for a human to weigh &mdash; never verdicts. A shared IP is usually a
        household, an office or a carrier NAT; high volume is often legitimate. Nothing here takes an
        enforcement action.
      </PageHeader>

      <div className="mb-5 border-b border-line">
        <Tabs items={TABS} value={tab} onChange={setTab} label="Fraud signals" panelId={PANEL_ID} />
      </div>

      {/* tabIndex={0}: the Referral panel has no focusable descendants, so without it a keyboard
          user tabbing off the tablist would skip the panel entirely and never reach its content. */}
      <div role="tabpanel" id={PANEL_ID} aria-labelledby={`tab-${tab}`} tabIndex={0}>
        {tab === 'referrals' && <ReferralPanel />}
        {tab === 'velocity' && <VelocityPanel />}
        {tab === 'invite-bursts' && <InviteBurstPanel />}
        {tab === 'refunds' && <RefundPanel />}
      </div>
    </>
  );
}

/**
 * The shared list scaffold: intro, the three data states, the rows and the pager. Each panel supplies
 * its own row markup; everything around the rows is identical so the four queues read as one screen.
 */
interface FraudListProps<T> {
  query: UseQueryResult<Page<T>>;
  offset: number;
  onOffsetChange: (offset: number) => void;
  intro: string;
  noun: string;
  empty: { title: string; body: string };
  rowKey: (item: T) => string;
  renderRow: (item: T) => React.ReactNode;
}

function FraudList<T>({
  query,
  offset,
  onOffsetChange,
  intro,
  noun,
  empty,
  rowKey,
  renderRow,
}: FraudListProps<T>) {
  return (
    <>
      <p className="mb-4 max-w-[76ch] font-body text-[12.5px] leading-relaxed text-ink-70">
        {intro}
      </p>

      {query.isPending && <Loading label="Loading…" />}
      {query.isError && <ErrorState onRetry={() => query.refetch()} />}

      {query.isSuccess &&
        (query.data.items.length === 0 ? (
          <EmptyState title={empty.title}>{empty.body}</EmptyState>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {query.data.items.map((item) => (
                <li
                  key={rowKey(item)}
                  className="rounded-md border border-line bg-surface px-4 py-3"
                >
                  {renderRow(item)}
                </li>
              ))}
            </ul>
            <Pager
              offset={offset}
              total={query.data.total}
              count={query.data.items.length}
              onOffsetChange={onOffsetChange}
              noun={noun}
            />
          </>
        ))}
    </>
  );
}

/** The row's top line: primary text left, badges right. Shared by every panel. */
function RowHead({ primary, secondary, badges }: {
  primary: React.ReactNode;
  secondary: React.ReactNode;
  badges: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-display text-[13.5px] font-medium text-ink">{primary}</div>
        <p className="mt-0.5 font-body text-[11px] text-ink-70">{secondary}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">{badges}</div>
    </div>
  );
}

function ReferralPanel() {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['fraud-referrals', offset],
    queryFn: () => fetchFraudFlags(offset),
  });

  return (
    <FraudList<ReferralFraudFlag>
      query={query}
      offset={offset}
      onOffsetChange={setOffset}
      intro="Referral grants where the referrer and the referred person share a sign-in IP or a registered device, newest first. The counts are of distinct addresses and devices — forty sessions from one home IP count as one."
      noun="flag"
      empty={{ title: 'No flags', body: 'No referral grants show a shared IP or device.' }}
      rowKey={(flag) => flag.grantId}
      renderRow={(flag) => {
        const ips = flag.sharedIpCount;
        const devices = flag.sharedDeviceCount;
        return (
          <RowHead
            primary={
              <>
                {flag.referrerDisplayName}{' '}
                <span className="font-normal text-ink-70">referred</span>{' '}
                {flag.referredDisplayName}
              </>
            }
            secondary={<>Granted {formatTimestamp(flag.grantedAt)}</>}
            badges={
              <>
                {ips > 0 && (
                  <Badge tone="neutral">
                    {ips} shared {ips === 1 ? 'address' : 'addresses'}
                  </Badge>
                )}
                {devices > 0 && (
                  <Badge tone="coral">
                    {devices} shared {devices === 1 ? 'device' : 'devices'}
                  </Badge>
                )}
              </>
            }
          />
        );
      }}
    />
  );
}

function VelocityPanel() {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['fraud-velocity', offset],
    queryFn: () => fetchVelocityFlags(offset),
  });

  return (
    <FraudList<VelocityFlag>
      query={query}
      offset={offset}
      onOffsetChange={setOffset}
      intro="Sign-in IPs from which several distinct accounts have authenticated inside the review window, most-clustered first — the burner-account / referral-farming signal. A university, office or carrier NAT clears the threshold legitimately."
      noun="address"
      empty={{
        title: 'No shared IPs',
        body: 'No sign-in IP is shared by enough accounts to flag.',
      }}
      rowKey={(flag) => flag.sharedIp}
      renderRow={(flag) => (
        <div className="flex flex-col gap-2.5">
          <RowHead
            primary={<span className="font-mono text-[13px]">{flag.sharedIp}</span>}
            secondary={
              <>
                First seen {formatTimestamp(flag.earliestSignInAt)} &middot; last{' '}
                {formatTimestamp(flag.latestSignInAt)}
              </>
            }
            badges={
              <Badge tone="neutral">
                {flag.accountCount} {flag.accountCount === 1 ? 'account' : 'accounts'}
              </Badge>
            }
          />
          <LinkedAccountsDisclosure userId={flag.sampleAccountId} note="a sample account on this IP" />
        </div>
      )}
    />
  );
}

function InviteBurstPanel() {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['fraud-invite-bursts', offset],
    queryFn: () => fetchInviteBurstFlags(offset),
  });

  return (
    <FraudList<InviteBurstFlag>
      query={query}
      offset={offset}
      onOffsetChange={setOffset}
      intro="Candidates who created an unusual number of reference requests inside the review window, busiest first — the detective companion to the per-candidate create limit, surfacing volume before the hard cap trips."
      noun="candidate"
      empty={{
        title: 'No bursts',
        body: 'No candidate is over the request-velocity threshold.',
      }}
      rowKey={(flag) => flag.candidateUserId}
      renderRow={(flag) => (
        <div className="flex flex-col gap-2.5">
          <RowHead
            primary={flag.displayName}
            secondary={
              <>
                First {formatTimestamp(flag.earliestRequestAt)} &middot; last{' '}
                {formatTimestamp(flag.latestRequestAt)}
              </>
            }
            badges={
              <Badge tone="neutral">
                {flag.requestCount} {flag.requestCount === 1 ? 'request' : 'requests'}
              </Badge>
            }
          />
          <LinkedAccountsDisclosure userId={flag.candidateUserId} note="this candidate's account" />
        </div>
      )}
    />
  );
}

function RefundPanel() {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['fraud-refunds', offset],
    queryFn: () => fetchRefundFlags(offset),
  });

  return (
    <FraudList<RefundAbuseFlag>
      query={query}
      offset={offset}
      onOffsetChange={setOffset}
      intro="Users whose count of refunded purchases across the Stripe and IAP rails together is at or over the review threshold, most-refunded first. Read-only — refunds are not reversed here."
      noun="user"
      empty={{
        title: 'No refund flags',
        body: 'No user is at or over the refunded-purchase threshold.',
      }}
      rowKey={(flag) => flag.userId}
      renderRow={(flag) => (
        <div className="flex flex-col gap-2.5">
          <RowHead
            primary={flag.displayName}
            secondary={
              <>
                Last refund{' '}
                {flag.latestRefundAt ? formatTimestamp(flag.latestRefundAt) : '—'}
              </>
            }
            badges={
              <>
                <Badge tone="neutral">
                  {flag.totalRefundCount} {flag.totalRefundCount === 1 ? 'refund' : 'refunds'}
                </Badge>
                {flag.stripeRefundCount > 0 && (
                  <Badge tone="neutral">Stripe {flag.stripeRefundCount}</Badge>
                )}
                {flag.iapRefundCount > 0 && <Badge tone="neutral">IAP {flag.iapRefundCount}</Badge>}
              </>
            }
          />
          <LinkedAccountsDisclosure userId={flag.userId} note="this account" />
        </div>
      )}
    />
  );
}
